import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Minus, Loader, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useProjects } from '../../contexts/ProjectsContext';
import { useReports } from '../../contexts/ReportsContext';
import { getWorkingDaysInMonth } from '../../utils/dateHelpers';

export const TimesheetForm = ({ onClose }) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { projects, loadingProjects } = useProjects();
  const { timesheetData, setTimesheetData, submitTimesheet } = useReports();
  const [submitting, setSubmitting] = useState(false);
  const [searchQueries, setSearchQueries] = useState({});
  const [filteredProjectsMap, setFilteredProjectsMap] = useState({});

  // Обновление поискового запроса для конкретного индекса
  const updateSearchQuery = (index, query) => {
    setSearchQueries(prev => ({ ...prev, [index]: query }));
    
    if (query.trim() === '') {
      setFilteredProjectsMap(prev => ({ ...prev, [index]: [] }));
    } else {
      const filtered = projects.filter(project =>
        project.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredProjectsMap(prev => ({ ...prev, [index]: filtered }));
    }
  };

  // Показать все проекты при фокусе, если поле пустое
  const handleFocus = (index) => {
    if (!timesheetData[index]?.project) {
      setFilteredProjectsMap(prev => ({ ...prev, [index]: projects }));
    }
  };

  const handleAddEntry = () => {
    const newIndex = timesheetData.length;
    setTimesheetData([...timesheetData, { project: '', days: '' }]);
    setSearchQueries(prev => ({ ...prev, [newIndex]: '' }));
    setFilteredProjectsMap(prev => ({ ...prev, [newIndex]: [] }));
  };
  
  const handleRemoveEntry = (index) => {
    if (timesheetData.length > 1) {
      const newData = [...timesheetData];
      newData.splice(index, 1);
      setTimesheetData(newData);
      
      // Очищаем поисковые данные для удалённого индекса
      const newQueries = { ...searchQueries };
      const newFiltered = { ...filteredProjectsMap };
      delete newQueries[index];
      delete newFiltered[index];
      setSearchQueries(newQueries);
      setFilteredProjectsMap(newFiltered);
    }
  };
  
  const handleEntryChange = (index, field, value) => {
    const newData = [...timesheetData];
    newData[index][field] = value;
    setTimesheetData(newData);
    
    if (field === 'project') {
      updateSearchQuery(index, value);
    }
  };

  const handleProjectSelect = (index, project) => {
    handleEntryChange(index, 'project', project);
    // Очищаем выпадающий список после выбора
    setFilteredProjectsMap(prev => ({ ...prev, [index]: [] }));
    setSearchQueries(prev => ({ ...prev, [index]: '' }));
  };

  const handleSubmit = async () => {
    const validEntries = timesheetData.filter(entry =>
      entry.project.trim() && entry.days.trim() && !isNaN(entry.days) && parseFloat(entry.days) > 0
    );
    if (validEntries.length === 0) {
      alert('Пожалуйста, заполните хотя бы один проект');
      return;
    }
    setSubmitting(true);
    const result = await submitTimesheet(validEntries, currentUser?.name);
    setSubmitting(false);
    if (result.success) {
      alert('Отчеты успешно отправлены!');
      setTimesheetData([{ project: '', days: '' }]);
      setSearchQueries({});
      setFilteredProjectsMap({});
      onClose();
    } else {
      alert(`Ошибка: ${result.error}`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center p-0 md:p-4 z-50"
      onClick={onClose}
    >
      <div
        className={`w-full max-h-[90vh] overflow-y-auto rounded-t-xl md:rounded-xl p-5 md:p-6 ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        } md:max-w-2xl`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg md:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            TimeSheet Отчеты
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors touch-target hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Закрыть"
          >
            <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
        
        {loadingProjects ? (
          <div className="flex justify-center items-center py-8">
            <Loader className="w-6 h-6 animate-spin text-blue-500" />
            <span className="ml-2 text-sm">Загрузка проектов...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {timesheetData.map((entry, index) => (
              <div key={index} className={`p-4 rounded-lg border ${
                darkMode ? 'border-gray-700 bg-gray-700/50' : 'border-gray-200 bg-gray-50'
              }`}>
                <div className="flex justify-between items-start mb-3">
                  <h3 className={`font-medium text-sm md:text-base ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Проект #{index + 1}
                  </h3>
                  {timesheetData.length > 1 && (
                    <button
                      onClick={() => handleRemoveEntry(index)}
                      className={`p-1 rounded-full touch-target ${
                        darkMode ? 'text-red-400 hover:bg-red-900/50' : 'text-red-500 hover:bg-red-50'
                      }`}
                      title="Удалить проект"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {/* Поле проекта с автодополнением */}
                  <div className="relative">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Проект
                    </label>
                    <input
                      type="text"
                      value={entry.project}
                      onChange={(e) => handleEntryChange(index, 'project', e.target.value)}
                      onFocus={() => handleFocus(index)}
                      className={`w-full px-4 py-3 text-base rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                      }`}
                      placeholder="Начните вводить название проекта..."
                      autoComplete="off"
                    />
                    {filteredProjectsMap[index]?.length > 0 && (
                      <div className={`absolute z-10 w-full mt-1 rounded-lg shadow-lg max-h-60 overflow-y-auto ${
                        darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-300'
                      }`}>
                        {filteredProjectsMap[index].map((project, idx) => (
                          <div
                            key={idx}
                            onClick={() => handleProjectSelect(index, project)}
                            className={`px-4 py-2 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 ${
                              entry.project === project ? 'bg-blue-50 dark:bg-blue-900/50 font-medium' : ''
                            }`}
                          >
                            {project}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Количество дней */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Количество дней
                    </label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={entry.days}
                      onChange={(e) => handleEntryChange(index, 'days', e.target.value)}
                      className={`w-full px-4 py-3 text-base rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                      }`}
                      placeholder="Например: 5.5"
                    />
                    <p className={`mt-1 text-xs ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      * При {getWorkingDaysInMonth(new Date())} рабочих днях, 1 день = {(100 / getWorkingDaysInMonth(new Date())).toFixed(2)}%
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Кнопка добавления нового проекта */}
            <button
              onClick={handleAddEntry}
              className={`w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-lg border-2 touch-target ${
                darkMode
                  ? 'border-dashed border-blue-500 text-blue-400 hover:bg-blue-900/30'
                  : 'border-dashed border-blue-300 text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm">Добавить еще проект</span>
            </button>
            
            {/* Кнопки действий */}
            <div className={`flex flex-col-reverse xs:flex-row justify-end space-y-reverse space-y-2 xs:space-y-0 xs:space-x-3 pt-6 border-t ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <button
                onClick={onClose}
                className={`w-full xs:w-auto px-6 py-3 rounded-lg transition-colors touch-target ${
                  darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Отмена
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`w-full xs:w-auto px-6 py-3 rounded-lg transition-colors flex items-center justify-center touch-target ${
                  darkMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
                } ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {submitting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    <span>Отправка...</span>
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    <span>Отправить ({timesheetData.filter(e => e.project && e.days).length})</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};